import { useCallback, useMemo } from 'react'
import { useSearchesQuery } from '~/api'
import {
  aDrawnFeature,
  aMapDrawIsOpen,
  aMapDrawRef,
  aMapFilters,
  aUser,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import { Badge, Button } from '~/components/ui'
import { useDebounce, useMapCardSizes } from '~/hooks'
import type { Search } from '~/models'
import { getMapFilterLabels } from '~/models'
import type { AppMapRef } from '../../google'
import Actions from './Actions'
import { getSavedSearchDrawFeature, openSavedSearch } from './openSavedSearch'

function SavedSearchRow({
  searchQueryKey,
  search,
  openSearch,
}: {
  searchQueryKey: string[]
  search: Search
  openSearch: (search: Search) => void
}) {
  const user = useAtomValue(aUser)
  const { updateSearchData } = useSearchesQuery({
    key: searchQueryKey,
    find: {
      'user._id': user!._id,
    },
  })

  const isDrawnFeature = useMemo(() => {
    return Boolean(getSavedSearchDrawFeature(search))
  }, [search.location])

  return (
    <div key={search._id} className="rounded-sm border p-4 shadow-sm">
      <Button
        variant="link"
        onClick={() => openSearch(search)}
        className="-mt-1 h-auto p-0 text-left">
        <span className="overflow-ellipsis font-medium">{search.name}</span>
      </Button>

      {search.description && (
        <p className="mt-1 text-sm">{search.description}</p>
      )}
      <div className="mt-2 flex flex-wrap gap-2">
        {isDrawnFeature && (
          <Badge variant="secondary" className="px-1 text-xs">
            Map Geography
          </Badge>
        )}
        {getMapFilterLabels(search.filters).map((label) => (
          <Badge key={label} variant="secondary" className="px-1 text-xs">
            {label}
          </Badge>
        ))}
      </div>
      <Actions search={search} updateSearchData={updateSearchData} />
    </div>
  )
}

export default function SavedSearchesList({
  searchQueryKey,
  filter,
  searches,
  mapRef,
  setIsOpen,
}: {
  searchQueryKey: string[]
  filter: string
  searches: Search[]
  mapRef: React.RefObject<AppMapRef>
  setIsOpen: (isOpen: boolean) => void
}) {
  const setDrawnFeature = useSetAtom(aDrawnFeature)
  const mapDrawRef = useAtomValue(aMapDrawRef)
  const setIsDrawOpen = useSetAtom(aMapDrawIsOpen)
  const setMapFilters = useSetAtom(aMapFilters)
  const debouncedFilter = useDebounce(filter, 200)
  const mapCardSizes = useMapCardSizes()

  const filteredSearches = useMemo(() => {
    if (debouncedFilter.length === 0) return searches
    const filterParts = debouncedFilter
      .toLowerCase()
      .split(' ')
      .filter((v) => v)
    return searches.filter((search) => {
      return [
        search.name,
        search.description || '',
        ...Object.keys(search.filters),
      ]
        .map((v) => v.toLowerCase())
        .some((v) => filterParts.some((v2) => v.includes(v2)))
    })
  }, [searches, debouncedFilter])

  const openSearch = useCallback(
    (search: Search) => {
      setIsOpen(false)
      setMapFilters(search.filters)
      openSavedSearch({
        map: mapRef.current,
        mapDrawRef,
        padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
        search,
        setDrawnFeature,
        setIsDrawOpen,
      })
    },
    [
      mapDrawRef,
      mapCardSizes.list,
      mapRef,
      setDrawnFeature,
      setIsDrawOpen,
      setIsOpen,
      setMapFilters,
    ]
  )

  if (searches.length === 0) {
    return (
      <p className="mt-4 italic">
        No saved searches. Create a new one to get started.
      </p>
    )
  }
  return (
    <div className="flex flex-col gap-4">
      {filteredSearches.length === 0 && (
        <p className="text-sm italic">No saved searches match your filter.</p>
      )}
      {filteredSearches.map((search) => {
        return (
          <SavedSearchRow
            key={search._id}
            searchQueryKey={searchQueryKey}
            search={search}
            openSearch={openSearch}
          />
        )
      })}
    </div>
  )
}
